import type { ComponentType} from 'react';
import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { Gift } from '@uikit/icons/Gift';
import KarmaStarColoredIcon from '@uikit/icons/KarmaStarColored';
import { UsdtCircle } from '@uikit/icons/UsdtCircle';

import type {
  RewardPool,
  RewardPoolCurrency} from '@shared/api/types/rewardPools';
import {
  REWARD_POOL_CURRENCY,
  REWARD_POOL_DISTRIBUTION_POLICY,
  RewardPoolCurrencyConfig,
} from '@shared/api/types/rewardPools';
import { MsqLogo } from '@shared/constants/icons';
import { pluralizeWord } from '@shared/helpers/formatters';
import { formatAmountDisplay } from '@shared/helpers/number';

import { cn } from '@ui/uikit/utils';

type RewardViewConfigItem = {
  Icon?: ComponentType<{ className?: string }>;
  iconClassName?: string;
  bgClassName: string;
  borderColorClassName: string;
};

const REWARD_VIEW_CONFIG: Record<RewardPoolCurrency, RewardViewConfigItem> = {
  [REWARD_POOL_CURRENCY.SQR]: {
    Icon: MsqLogo,
    bgClassName:
      'bg-[linear-gradient(206.57deg,rgba(255,128,102,0.16)_0%,rgba(229,102,255,0.16)_41.67%,rgba(102,178,255,0.16)_83.33%)]',
    borderColorClassName: 'border-base-border-pink-medium',
  },
  [REWARD_POOL_CURRENCY.USDT]: {
    Icon: UsdtCircle,
    bgClassName: 'bg-[linear-gradient(225deg,#E6FAE9_0%,#EEFBF0_50%,#E6FAE9_100%)]',
    borderColorClassName: 'border-base-border-green-medium',
  },
  [REWARD_POOL_CURRENCY.KARMA]: {
    Icon: KarmaStarColoredIcon,
    bgClassName: 'bg-[linear-gradient(225deg,#FCEDF5_0%,#FEF6FA_50%,#FCEDF5_100%)]',
    borderColorClassName: 'border-base-border-pink-medium',
  },
  [REWARD_POOL_CURRENCY.CUSTOM_REWARD]: {
    Icon: Gift,
    bgClassName: 'bg-[linear-gradient(225deg,#FAEDFC_0%,#FCF6FE_50%,#FAEDFC_100%)]',
    borderColorClassName: 'border-base-border-purple-medium',
    iconClassName: 'text-base-text-purple',
  },
};

const getRewardTitle = (rewardPool: RewardPool): string => {
  if (rewardPool?.reward_currency === REWARD_POOL_CURRENCY.CUSTOM_REWARD) {
    return rewardPool.custom_reward_options?.title ?? '';
  }

  return RewardPoolCurrencyConfig[rewardPool.reward_currency].title;
};

const getFormatedReward = (rewardPool: RewardPool): string => {
  const title = getRewardTitle(rewardPool);

  if (rewardPool.reward_currency === REWARD_POOL_CURRENCY.CUSTOM_REWARD) {
    return title;
  }

  return `${formatAmountDisplay(rewardPool.reward_amount || 0)} ${title}`;
};

export const getRewardPoolReward = ({
  rewardPool,
  selfReward,
}: {
  rewardPool: RewardPool;
  selfReward?: number | null;
}) => {
  const title = getRewardTitle(rewardPool);

  const formattedSelfReward = `${formatAmountDisplay(selfReward || 0)} ${title}`;
  const rewardPerWinner =
    (rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE ||
      rewardPool.distribution_policy === REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2) &&
    rewardPool.number_of_winners ? (
      <>
        {formatAmountDisplay(rewardPool.reward_amount / rewardPool.number_of_winners)} {title} ×{' '}
        {rewardPool.number_of_winners} {pluralizeWord('Winner', rewardPool.number_of_winners)}
      </>
    ) : null;

  const configItem = REWARD_VIEW_CONFIG[rewardPool.reward_currency];

  return {
    id: rewardPool.reward_currency,
    formattedReward: getFormatedReward(rewardPool),
    formattedSelfReward,
    rewardPerWinner: rewardPerWinner,
    Icon: configItem.Icon,
    iconClassName: configItem.iconClassName,
    bgClassName: configItem.bgClassName,
    borderColorClassName: configItem.borderColorClassName,
  };
};

interface RewardPoolsCardProps {
  rewardPool: RewardPool;
}

const PIXELS_PER_SECOND = 50;

const getAnimationStyle = (duration: number) => {
  return duration > 0 ? { animation: `infinite-scroll ${duration}s linear infinite` } : undefined;
};

type RewardItemProps = {
  reward: string;
  animationDuration: number;
  Icon?: ComponentType<{ className?: string }>;
  iconClassName?: string;
};

const RewardItem = ({ reward, animationDuration, Icon, iconClassName }: RewardItemProps) => {
  return (
    <div
      key={1}
      className={classNames('mx-auto font-semibold whitespace-nowrap text-[#09090B] px-[3px]', {
        'max-xl:text-sm': reward.length > 1,
      })}
      style={getAnimationStyle(animationDuration)}
    >
      <div
        className="bg-neutral-50 gap-1.5 min-h-10 px-4 inline-flex items-center justify-center text-center rounded-full py-1"
        data-qa="reward_amount"
      >
        <>
          {Icon && <Icon className={cn('icon-xs', iconClassName)} />}
          {reward}
        </>
      </div>
    </div>
  );
};

export const RewardPoolCardReward = ({ rewardPool }: RewardPoolsCardProps) => {
  const { Icon, iconClassName, formattedReward } = getRewardPoolReward({ rewardPool });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const [animationDuration, setAnimationDuration] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !scrollableRef.current) {
      return;
    }

    const containerWidth = containerRef.current.clientWidth;
    const scrollableWidth = scrollableRef.current.clientWidth;

    if (scrollableWidth > containerWidth) {
      const duration = scrollableWidth / PIXELS_PER_SECOND;
      setAnimationDuration(duration);
    }
  }, []);

  const needAnimation = animationDuration > 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-[7.5rem] px-1.5 overflow-hidden flex items-center bg-gradient-to-b from-[rgba(255,128,102,0.16)] via-[rgba(229,102,255,0.16)]to-[rgba(102,178,255,0.16)]',
        !needAnimation && 'justify-center',
      )}
    >
      <div ref={scrollableRef} className="flex">
        <RewardItem
          reward={formattedReward}
          animationDuration={animationDuration}
          Icon={Icon}
          iconClassName={iconClassName}
        />
        {needAnimation && (
          <RewardItem
            reward={formattedReward}
            animationDuration={animationDuration}
            Icon={Icon}
            iconClassName={iconClassName}
          />
        )}
      </div>
    </div>
  );
};
